import React, { Fragment, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import Modal from 'reactstrap/lib/Modal';
import { get } from 'lodash-es';
import { connect } from 'react-redux';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { UserProfileModel } from 'client/data/models/profile/profile';
import { VisitorModel } from 'client/data/models/visitor';
import { createDataToSubscriptions } from 'site-modules/shared/utils/profile/email-subscriptions-helper';
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';
import { EdmundsLogoText } from 'site-modules/shared/components/logos/edmunds-logo-text';
import { SignEmailForm } from 'site-modules/shared/components/profile/smart-sign/sign-email-form/sign-email-form';
import { LoadingSpinner } from 'site-modules/shared/components/loading-spinner/loading-spinner';

import './news-subscription-button.scss';

const NEWS_SUBSCRIPTION = 'content_series';
const MODAL_CREATIVE_ID = 'car_news_subscribe_form';

export function NewsSubscriptionButtonUI({
  profileEmail = null,
  visitorId = null,
  isAuthenticated = false,
  createOrUpdateIdmData,
  removeDataFromIdm,
  className = null,
  isMobile = false,
}) {
  const [openModal, toggleOpenModal] = useToggle(false);
  const [isLoading, setLoading] = useState(false);
  const [isSubscribed, setSubscribed] = useState(false);

  useEffect(() => {
    if (openModal) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
        event_data: {
          action_name: TrackingConstant.ACTION_WIDGET_VIEW,
          action_cause: TrackingConstant.ACTION_CAUSE_DRAWER_OPEN,
          creative_id: MODAL_CREATIVE_ID,
        },
      });
    }
  }, [openModal]);

  const fireEmailEnter = useCallback(({ target: { value } }) => {
    if (!value) {
      return;
    }

    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
      event_data: {
        action_name: TrackingConstant.ACTION_SIGN_UP,
        subaction_name: TrackingConstant.PROVIDE_EMAIL,
        action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        creative_id: MODAL_CREATIVE_ID,
      },
    });
  }, []);

  const addEmailTracking = useCallback(() => {
    document.getElementById('sign-up-email')?.addEventListener('blur', fireEmailEnter);
  }, [fireEmailEnter]);

  useEffect(() => {
    if (!openModal) {
      document.getElementById('sign-up-email')?.removeEventListener('blur', fireEmailEnter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal]);

  const onSubmit = useCallback(
    async ({ emailValue }) => {
      setLoading(true);

      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
        event_data: {
          action_name: TrackingConstant.ACTION_SIGN_UP,
          subaction_name: TrackingConstant.SUCCESS_EMAIL_ONLY_SIGN_UP,
          action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          creative_id: MODAL_CREATIVE_ID,
        },
      });

      try {
        if (profileEmail && emailValue === profileEmail) {
          await removeDataFromIdm(createDataToSubscriptions([NEWS_SUBSCRIPTION], 'subscribe').values);
        } else {
          await createOrUpdateIdmData({ idmDataToSave: { identifiers: { email: [emailValue] } } });
        }

        setLoading(false);
        setSubscribed(true);
      } catch {
        setLoading(false);
      }
    },
    [profileEmail, removeDataFromIdm, createOrUpdateIdmData]
  );

  return (
    <Fragment>
      <div className={classnames('news-subscription-button', className)} data-tracking-parent="car_news_newsletter">
        <div className="heading-5 mb-0_75">Get More Edmunds Car News in Your Inbox</div>
        <Button
          color="primary-b"
          size="lg"
          className={classnames('py-0_5 px-1 text-transform-none text-decoration-none text-white px-md-2 fw-normal', {
            'w-100': isMobile,
          })}
          onClick={toggleOpenModal}
          data-tracking-id="begin_sign_up"
          data-tracking-value={visitorId}
        >
          Subscribe
        </Button>
      </div>
      <Modal
        isOpen={openModal}
        toggle={toggleOpenModal}
        onOpened={addEmailTracking}
        className="news-subscription-modal"
        contentClassName="p-1_5 pos-r"
        data-tracking-parent="car_news_subscribe_form"
      >
        <div className="edmunds-logo mt-0_75 mb-2">
          <EdmundsLogoText />
        </div>
        {isSubscribed ? (
          <div className="size-20">You are subscribed, thank you!</div>
        ) : (
          <LoadingSpinner isLoading={isLoading}>
            <div className="size-20 mb-1_5">
              Enter your email to get Edmunds Car News delivered to your inbox every week.
            </div>
            <SignEmailForm
              isAuthenticated={isAuthenticated}
              successCallback={onSubmit}
              profileDataEmail={profileEmail}
              isVerifyEmail={false}
              ctaText="Subscribe"
              isSignInAction
            />
          </LoadingSpinner>
        )}
        <button
          className="close-btn size-20 pos-a top-0 right-0 mt-1 me-1 p-0_5 border-0 bg-transparent"
          onClick={toggleOpenModal}
        >
          <span className="visually-hidden">Close modal</span>
          <i className="icon-cross2" aria-hidden />
        </button>
      </Modal>
    </Fragment>
  );
}

NewsSubscriptionButtonUI.propTypes = {
  createOrUpdateIdmData: PropTypes.func.isRequired,
  removeDataFromIdm: PropTypes.func.isRequired,
  profileEmail: PropTypes.string,
  visitorId: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  className: PropTypes.string,
  isMobile: PropTypes.bool,
};

const mapStateToProps = state => ({
  isAuthenticated: get(state, 'profile.isAuthenticated'),
});

export const NewsSubscriptionButton = connect(mapStateToProps)(
  connectToModel(insiderMethods(NewsSubscriptionButtonUI), {
    profileEmail: bindToPath(
      ({ isAuthenticated }) => (isAuthenticated ? 'data.idm' : ''),
      UserProfileModel,
      profileData => profileData?.identifiers?.email?.[0]
    ),
    visitorId: bindToPath('id', VisitorModel),
  })
);
